import {
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { Alert } from "@material-ui/lab";
import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import cuideoTheme from "../../../../../../containers/themes/defaultTheme";
import { useAllowedAttachmentExtensions } from "../../api/getAllowedAttachmentExtensions";
import { useUploadCandidateAttachment } from "../../api/uploadCandidateAttachment";
import {
  findInvalidFiles,
  formatFileSize,
} from "../../util/candidateAttachment.util";

const StyledTitle = styled(Typography)`
  color: ${cuideoTheme.palette.primary.main};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 1rem;
`;

const StyledContent = styled.div`
  margin: 0 1rem;
`;

const StyledDropZone = styled.div<{ $isDragging: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  border: 2px dashed
    ${({ $isDragging }) =>
      $isDragging
        ? cuideoTheme.palette.primary.main
        : "rgba(0, 72, 120, 0.25)"};
  border-radius: ${cuideoTheme.shape.borderRadius}px;
  background-color: ${({ $isDragging }) =>
    $isDragging ? "rgba(0, 72, 120, 0.05)" : "transparent"};
  color: ${cuideoTheme.palette.primary.main};
  text-align: center;
`;

const StyledUploadIcon = styled(CloudUploadIcon)`
  font-size: 2.5rem;
  opacity: 0.5;
`;

const StyledDropText = styled(Typography)`
  margin: 0.5rem 0;
  font-size: 1rem;
`;

const StyledHint = styled(Typography)`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: ${cuideoTheme.palette.primary.light};
`;

const StyledFileList = styled.ul`
  list-style: none;
  margin: 1rem 0 0;
  padding: 0;
`;

const StyledFileItem = styled.li`
  display: flex;
  align-items: center;
  padding: 0.25rem 0;
  border-bottom: 1px solid rgba(0, 72, 120, 0.1);
  color: ${cuideoTheme.palette.primary.main};
  font-size: 0.875rem;
`;

const StyledFileName = styled.span`
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledFileSize = styled.span`
  margin: 0 0.5rem;
  white-space: nowrap;
  opacity: 0.75;
`;

const StyledAlert = styled(Alert)`
  margin-top: 1rem;
`;

const StyledDialogActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 1rem 0.5rem 0.5rem;
`;

const StyledActionButton = styled(Button)`
  margin: 0.5rem;
  flex-grow: 1;
`;

const HTTP_BAD_REQUEST = 400;
const HTTP_PAYLOAD_TOO_LARGE = 413;

interface IUploadCandidateAttachmentsDialogProps {
  open: boolean;
  candidateId: string;
  onClose: () => void;
  onUploaded: () => void;
}

/**
 * ECO-1416: picks or drops one or more files and uploads them to the candidate.
 * Every extension is checked before sending anything. Files are sent one per
 * request, because the server limits the size of each request.
 */
const UploadCandidateAttachmentsDialog = (
  props: IUploadCandidateAttachmentsDialogProps
) => {
  const { open, candidateId, onClose, onUploaded } = props;

  const { formatMessage } = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const allowedExtensionsQuery = useAllowedAttachmentExtensions();
  const uploadMutation = useUploadCandidateAttachment();

  // Every opening starts from scratch
  useEffect(() => {
    if (open) {
      setFiles([]);
      setErrors([]);
      setIsDragging(false);
    }
  }, [open]);

  const allowedExtensions = allowedExtensionsQuery.data ?? [];
  const accept = allowedExtensions
    .map((extension) => `.${extension}`)
    .join(",");

  const addFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      return;
    }

    const newFiles = Array.from(fileList);
    setFiles((currentFiles) => [...currentFiles, ...newFiles]);
    setErrors([]);
  };

  const handleBrowseClick = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files);
    // Lets the same file be picked again after removing it
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isUploading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (!isUploading) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((currentFiles) => currentFiles.filter((file, i) => i !== index));
  };

  const getUploadErrorMessage = (error: any, file: File): string => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;

    if (status === HTTP_BAD_REQUEST && typeof message === "string") {
      return message;
    }
    if (status === HTTP_PAYLOAD_TOO_LARGE) {
      return formatMessage(
        {
          id: "UploadCandidateAttachmentsDialog.El fichero «{fileName}» supera el tamaño máximo permitido",
          defaultMessage:
            "El fichero «{fileName}» supera el tamaño máximo permitido",
        },
        { fileName: file.name }
      );
    }

    return formatMessage(
      {
        id: "UploadCandidateAttachmentsDialog.No se ha podido subir el fichero «{fileName}»",
        defaultMessage: "No se ha podido subir el fichero «{fileName}»",
      },
      { fileName: file.name }
    );
  };

  const handleUploadClick = async () => {
    const invalidFiles = findInvalidFiles(files, allowedExtensions);
    if (invalidFiles.length > 0) {
      setErrors(
        invalidFiles.map(({ file, extension }) =>
          extension === ""
            ? formatMessage(
                {
                  id: "UploadCandidateAttachmentsDialog.El fichero «{fileName}» no tiene extensión y no está permitido",
                  defaultMessage:
                    "El fichero «{fileName}» no tiene extensión y no está permitido",
                },
                { fileName: file.name }
              )
            : formatMessage(
                {
                  id: "UploadCandidateAttachmentsDialog.El fichero «{fileName}» tiene una extensión no permitida ({extension})",
                  defaultMessage:
                    "El fichero «{fileName}» tiene una extensión no permitida ({extension})",
                },
                { fileName: file.name, extension }
              )
        )
      );
      return;
    }

    setErrors([]);
    setIsUploading(true);

    const failedFiles: File[] = [];
    const uploadErrors: string[] = [];
    for (const file of files) {
      try {
        await uploadMutation.mutateAsync({ candidateId, file });
      } catch (e) {
        failedFiles.push(file);
        uploadErrors.push(getUploadErrorMessage(e, file));
      }
    }

    setIsUploading(false);

    if (failedFiles.length === 0) {
      onUploaded();
      onClose();
      return;
    }

    // The uploaded files leave the list, so retrying does not send them twice
    setFiles(failedFiles);
    setErrors(uploadErrors);
  };

  const handleCancelClick = () => {
    onClose();
  };

  const isUploadDisabled =
    files.length === 0 ||
    isUploading ||
    allowedExtensionsQuery.isLoading ||
    allowedExtensionsQuery.isError;

  return (
    <Dialog
      open={open}
      onClose={isUploading ? undefined : onClose}
      className="MuiCuideo-UploadCandidateAttachmentsDialog"
      fullWidth
      maxWidth="sm"
    >
      <StyledTitle>
        <FormattedMessage
          id="UploadCandidateAttachmentsDialog.Añadir documentos"
          defaultMessage="Añadir documentos"
        />
      </StyledTitle>
      <StyledContent>
        <StyledDropZone
          $isDragging={isDragging}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-testid="candidate-attachments-drop-zone"
        >
          <StyledUploadIcon />
          <StyledDropText>
            <FormattedMessage
              id="UploadCandidateAttachmentsDialog.Arrastra aquí los ficheros o"
              defaultMessage="Arrastra aquí los ficheros o"
            />
          </StyledDropText>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleBrowseClick}
            disabled={isUploading}
          >
            <FormattedMessage
              id="UploadCandidateAttachmentsDialog.Examinar"
              defaultMessage="Examinar"
            />
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            accept={accept !== "" ? accept : undefined}
            onChange={handleInputChange}
            data-testid="candidate-attachments-file-input"
          />
        </StyledDropZone>
        {allowedExtensions.length > 0 && (
          <StyledHint>
            <FormattedMessage
              id="UploadCandidateAttachmentsDialog.Extensiones permitidas: {extensions}"
              defaultMessage="Extensiones permitidas: {extensions}"
              values={{ extensions: allowedExtensions.join(", ") }}
            />
          </StyledHint>
        )}
        {allowedExtensionsQuery.isError && (
          <StyledAlert severity="error">
            <FormattedMessage
              id="UploadCandidateAttachmentsDialog.No se han podido cargar las extensiones permitidas"
              defaultMessage="No se han podido cargar las extensiones permitidas"
            />
          </StyledAlert>
        )}
        {files.length > 0 && (
          <StyledFileList>
            {files.map((file, index) => (
              <StyledFileItem key={`${file.name}-${index}`}>
                <StyledFileName title={file.name}>{file.name}</StyledFileName>
                <StyledFileSize>{formatFileSize(file.size)}</StyledFileSize>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={formatMessage(
                    {
                      id: "UploadCandidateAttachmentsDialog.Quitar {fileName}",
                      defaultMessage: "Quitar {fileName}",
                    },
                    { fileName: file.name }
                  )}
                  onClick={() => {
                    handleRemoveFile(index);
                  }}
                  disabled={isUploading}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </StyledFileItem>
            ))}
          </StyledFileList>
        )}
        {errors.length > 0 && (
          <StyledAlert severity="error">
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </StyledAlert>
        )}
      </StyledContent>
      <StyledDialogActions>
        <StyledActionButton
          color="primary"
          variant="text"
          onClick={handleCancelClick}
          disabled={isUploading}
          fullWidth
        >
          <FormattedMessage
            id="UploadCandidateAttachmentsDialog.Cancelar"
            defaultMessage="Cancelar"
          />
        </StyledActionButton>
        <StyledActionButton
          color="primary"
          variant="contained"
          onClick={handleUploadClick}
          disabled={isUploadDisabled}
          fullWidth
        >
          {isUploading ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            <FormattedMessage
              id="UploadCandidateAttachmentsDialog.Subir"
              defaultMessage="Subir"
            />
          )}
        </StyledActionButton>
      </StyledDialogActions>
    </Dialog>
  );
};

export default UploadCandidateAttachmentsDialog;
